/**
 * 🌍 AIRPORT ZONE SERVICE
 * Manages zones for airports - CRUD operations
 */

import { buildApiUrl } from '@/utils/apiHelpers';

export interface DistanceRange {
  min: number;
  max: number;
  display: string; // e.g., "8-15km"
}

export interface ZoneStats {
  activeListingsCount: number;
}

export interface AirportZone {
  id: string;
  airportId: string;
  zoneName: string;
  zoneDescription: string;
  distanceRange: DistanceRange;
  landmarks: string;
  displayOrder: number;
  isActive: boolean;
  stats?: ZoneStats;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateZoneData {
  zoneName: string;
  zoneDescription: string;
  minDistanceKm: number;
  maxDistanceKm: number;
  landmarks: string;
  displayOrder?: number;
}

export interface UpdateZoneData {
  zoneName?: string;
  zoneDescription?: string;
  minDistanceKm?: number;
  maxDistanceKm?: number;
  landmarks?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface ZoneResponse {
  success: boolean;
  data?: AirportZone;
  zones?: AirportZone[];
  airport?: {
    id: string;
    airportCode: string;
    airportName: string;
    city: string;
  };
  error?: string;
  message?: string;
}

export const airportZoneService = {
  /**
   * 📥 Get all zones for an airport
   */
  async getZones(airportId: string): Promise<ZoneResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}/zones`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Zones API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch zones (${response.status})`);
      }

      const result = await response.json();

      // Handle nested response structure: { success: true, data: { zones: [...], airport: {...} } }
      const zones = result.zones || result.data?.zones || result.data || [];
      const airport = result.airport || result.data?.airport;

      return {
        success: true,
        zones: Array.isArray(zones) ? zones : [],
        airport: airport
      };
    } catch (error) {
      console.error('❌ Error fetching zones:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch zones'
      };
    }
  },

  /**
   * 📥 Get single zone by ID
   */
  async getZone(airportId: string, zoneId: string): Promise<ZoneResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}/zones/${zoneId}`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Zone API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch zone (${response.status})`);
      }

      const result = await response.json();

      // Handle nested response structure
      const zone = result.zone || result.data?.zone || result.data;

      return {
        success: true,
        data: zone
      };
    } catch (error) {
      console.error('❌ Error fetching zone:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch zone'
      };
    }
  },

  /**
   * ➕ Create new zone
   */
  async createZone(airportId: string, data: CreateZoneData): Promise<ZoneResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}/zones`, 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Zone API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to create zone (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      // Handle nested response structure
      const zone = result.zone || result.data?.zone || result.data;

      return {
        success: true,
        data: zone,
        message: result.message || 'Zone created successfully'
      };
    } catch (error) {
      console.error('❌ Error creating zone:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create zone'
      };
    }
  },

  /**
   * ✏️ Update zone
   */
  async updateZone(airportId: string, zoneId: string, data: UpdateZoneData): Promise<ZoneResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}/zones/${zoneId}`, 'v2');

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Zone API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to update zone (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      // Handle nested response structure
      const zone = result.zone || result.data?.zone || result.data;

      return {
        success: true,
        data: zone,
        message: result.message || 'Zone updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating zone:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update zone'
      };
    }
  },

  /**
   * 🗑️ Delete zone (soft delete)
   */
  async deleteZone(airportId: string, zoneId: string): Promise<ZoneResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}/zones/${zoneId}`, 'v2');

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete Zone API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to delete zone (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Zone deleted successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting zone:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete zone'
      };
    }
  },

  /**
   * 🔄 Reorder zones (update display order)
   */
  async reorderZones(airportId: string, zoneOrders: { zoneId: string; displayOrder: number }[]): Promise<ZoneResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}/zones/reorder`, 'v2');

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(zoneOrders)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Reorder Zones API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to reorder zones (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Zones reordered successfully'
      };
    } catch (error) {
      console.error('❌ Error reordering zones:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reorder zones'
      };
    }
  }
};
